// src/pages/MyPatientsPage.jsx

import { Flex, Grid, Select, Switch, Text } from '@radix-ui/themes';
import React, { useState, useEffect, useRef } from 'react';
import RoomCard from '../components/ui-components/RoomCard';
import CreateRoom from '../components/ui-components/CreateRoom';
import SOSButton from '../components/ui-components/SOSButton';
import MedicationPopup from '../components/ui-components/MedicationPopup';
import axiosInstance from '../config/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { getApiDateTimeMillis, parseApiDate } from '../utils/helpers';

const MyPatientsPage = () => {
  const [roomsData, setRoomsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bedAlarms, setBedAlarms] = useState({});
  const [bedHoldStates, setBedHoldStates] = useState({});
  const [bedMedicationStates, setBedMedicationStates] = useState({});
  const [bedSensorStatus, setBedSensorStatus] = useState({});
  const [bedDiaperStates, setBedDiaperStates] = useState({});
  const [showOnlyRoomsWithPatients, setShowOnlyRoomsWithPatients] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [activeRoomManagementBedId, setActiveRoomManagementBedId] = useState(null);

  const isComponentMountedRef = useRef(true);
  const processedAlertsRef = useRef(new Set());

  const [medicationPopup, setMedicationPopup] = useState({
    isOpen: false,
    medicationReminderId: null,
    medicationData: null,
    loading: false,
    error: null
  });

  const roleId = localStorage.getItem('roleId');
  const currentUserId = localStorage.getItem('userId');
  const { activeAlerts, clearAllAlerts, setActiveAlerts } = useAuth();
  const alarmSettings = JSON.parse(localStorage.getItem('alarmSettings')) || [];

  const handleNewAlarm = (bedId, masterAlarmId, alarmPriority, payload) => {
    try {
      const alarmConfig = alarmSettings.find((alarm) => alarm.masterAlarmId === masterAlarmId);
      if (!alarmConfig) return;

      // Use the admin-configured priority from alarmSettings as the source of truth.
      // Fall back to alarmPriority from the SignalR payload if settings priority is missing.
      const resolvedPriority = alarmConfig.priority != null ? Number(alarmConfig.priority) : Number(alarmPriority);

      const newAlarm = {
        bedId: bedId.toString(),
        masterAlarmId,
        alarmName: alarmConfig.alarmName,
        priority: resolvedPriority,
        color: alarmConfig.color,
        tunePath: alarmConfig.tunePath,
        timestamp: Date.now(),
        payload,
        isExtension: false
      };

      if (payload.batPercent !== undefined || payload.btPercent !== undefined) {
        setBedSensorStatus((prev) => ({
          ...prev,
          [bedId]: {
            batPercent: payload.batPercent || prev[bedId]?.batPercent || 0,
            btPercent: payload.btPercent || prev[bedId]?.btPercent || 0
          }
        }));
      }

      setBedAlarms((prev) => {
        const bedAlarmsList = prev[bedId] || [];
        const existingAlarms = bedAlarmsList.filter(a => a.masterAlarmId !== masterAlarmId);
        const updatedAlarms = [...existingAlarms, newAlarm].sort((a, b) => Number(a.priority) - Number(b.priority));

        return {
          ...prev,
          [bedId]: updatedAlarms
        };
      });
    } catch (error) {
      console.error('Error in handleNewAlarm:', error);
    }
  };

  const handleHoldExtension = (bedId, payload) => {
    if (!bedHoldStates[bedId]) return;

    const alarmConfig = alarmSettings.find((alarm) => alarm.masterAlarmId === payload.masterAlarmId);
    if (!alarmConfig) return;

    const newExtensionAlarm = {
      bedId: bedId.toString(),
      masterAlarmId: payload.masterAlarmId,
      alarmName: payload.alarmName,
      priority: 2,
      color: alarmConfig.color,
      tunePath: 'extension.mp3',
      timestamp: Date.now(),
      payload,
      isExtension: true
    };

    setBedAlarms((prev) => {
      const bedAlarmsList = prev[bedId] || [];
      const updatedAlarms = [...bedAlarmsList, newExtensionAlarm].sort((a, b) => Number(a.priority) - Number(b.priority));

      return {
        ...prev,
        [bedId]: updatedAlarms
      };
    });
  };

  const processInitialAlarmStates = (bedList) => {
    const newBedAlarms = {};
    const newBedHoldStates = {};
    const newBedMedicationStates = {};
    const newBedDiaperStates = {};
    const newBedSensorStatus = {};
    const activeAlertsToAdd = [];

    bedList.forEach((bed) => {
      const {
        id: bedId,
        alarmName,
        alarmStatus,
        alarmPriority,
        masterAlarmId,
        timeStamp,
        userId,
        reminderId,
        batPercent,
        btPercent
      } = bed;

      if (batPercent !== undefined || btPercent !== undefined) {
        newBedSensorStatus[bedId] = {
          batPercent: batPercent || 0,
          btPercent: btPercent || 0
        };
      }

      if (alarmStatus) {
        if (alarmStatus === 'Notified' && alarmName && masterAlarmId && alarmPriority) {
          const alarmConfig = alarmSettings.find((alarm) => alarm.masterAlarmId === masterAlarmId);
          if (alarmConfig) {
            const alarmDate = parseApiDate(timeStamp);
            // Use admin-configured priority as source of truth, fall back to bed data priority
            const resolvedPriority = alarmConfig.priority != null ? Number(alarmConfig.priority) : Number(alarmPriority);
            const initialAlarm = {
              bedId: bedId.toString(),
              masterAlarmId,
              alarmName,
              priority: resolvedPriority,
              color: alarmConfig.color,
              tunePath: alarmConfig.tunePath,
              timestamp: alarmDate ? alarmDate.getTime() : Date.now(),
              payload: {
                bedId,
                masterAlarmId,
                alarmPriority,
                reminderId,
                batPercent,
                btPercent
              },
              isExtension: false
            };
            newBedAlarms[bedId] = [initialAlarm];
            activeAlertsToAdd.push({
              alertType: 'Alarm',
              timestamp: alarmDate ? alarmDate.toISOString() : new Date().toISOString(),
              id: Date.now() + Math.random(),
              payload: {
                bedId: bedId.toString(),
                masterAlarmId,
                alarmPriority,
                alarmName,
                roomId: bed.roomNumber
              }
            });
          }
        } else if (alarmStatus === 'OnHold') {
          newBedHoldStates[bedId] = true;
        } else if (alarmStatus === 'Confirmed') {
          if (alarmName === 'Medication' && userId == currentUserId && reminderId) {
            newBedMedicationStates[bedId] = {
              isActive: true,
              medicationReminderId: reminderId
            };
          } else if (alarmName.toLowerCase().includes('diaper') || alarmName.toLowerCase().includes('wetsafe')) {
            newBedDiaperStates[bedId] = true;
          }
        }
      }
    });

    if (activeAlertsToAdd.length > 0) {
      setActiveAlerts(prev => {
        const newAlerts = [...prev];
        activeAlertsToAdd.forEach(alert => {
          // One entry per bed — only one alarm sound per bed at a time (intentional)
          if (!newAlerts.some(a => a.payload?.bedId === alert.payload.bedId)) {
            newAlerts.push(alert);
          }
        });
        return newAlerts;
      });
    }

    setBedAlarms(prev => {
      const merged = { ...prev };
      Object.keys(newBedAlarms).forEach(bedId => {
        const existingAlarms = merged[bedId] || [];
        const fetchedAlarms = newBedAlarms[bedId];
        const combined = [...existingAlarms, ...fetchedAlarms];
        const unique = [];
        const seen = new Set();
        combined.forEach(a => {
          if (!seen.has(a.masterAlarmId)) {
            seen.add(a.masterAlarmId);
            unique.push(a);
          }
        });
        merged[bedId] = unique.sort((a, b) => Number(a.priority) - Number(b.priority));
      });
      return merged;
    });
    setBedHoldStates(prev => ({ ...prev, ...newBedHoldStates }));
    setBedMedicationStates(prev => ({ ...prev, ...newBedMedicationStates }));
    setBedDiaperStates(prev => ({ ...prev, ...newBedDiaperStates }));
    setBedSensorStatus(prev => ({ ...prev, ...newBedSensorStatus }));
  };

  const fetchBeds = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await axiosInstance.get('/Bed/get-beds', {
        params: { token: null }
      });
      const bedList = response.data.result?.bedList || [];
      setRoomsData(bedList);
      processInitialAlarmStates(bedList);
    } catch (err) {
      setError('Failed to load patient data');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchBeds();
    return () => {
      isComponentMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axiosInstance.get('/user/get-departments');
        if (response.data.result) {
          const deptList = response.data.result || [];
          setDepartments(deptList);
          setSelectedDepartment('all');
        }
      } catch (err) {
        console.error('Failed to load departments', err);
      }
    };

    if (roleId === '2') {
      fetchDepartments();
    }
  }, [roleId]);

  useEffect(() => {
    console.log('MyPatientsPage - SignalR activeAlerts updated:', activeAlerts);

    if (activeAlerts.length > 0) {
      const newAlerts = activeAlerts.filter(alert => !processedAlertsRef.current.has(alert.id));

      if (newAlerts.length === 0) return;

      const sortedAlerts = [...newAlerts].sort((a, b) => {
        const timeA = getApiDateTimeMillis(a.timestamp || a['0']?.timestamp || 0);
        const timeB = getApiDateTimeMillis(b.timestamp || b['0']?.timestamp || 0);
        return timeA - timeB;
      });

      console.log('MyPatientsPage - Processing new alerts in chronological order:', newAlerts.length);

      sortedAlerts.forEach((alert) => {
        processSignalRAlert(alert);
        processedAlertsRef.current.add(alert.id);
      });

      if (processedAlertsRef.current.size > 100) {
        const idsArray = Array.from(processedAlertsRef.current);
        processedAlertsRef.current = new Set(idsArray.slice(-50));
      }
    }
  }, [activeAlerts]);

  const convertCamelCaseToWords = (text) => {
    return text.replace(/([A-Z])/g, ' $1').trim();
  };

  const refreshNotifications = async () => {
    try {
      window.dispatchEvent(new CustomEvent('refreshNotifications'));
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    }
  };

  const processSignalRAlert = (alert) => {
    console.log('MyPatientsPage - Processing SignalR alert:', alert);

    if (!alert || typeof alert !== 'object') {
      console.warn('Invalid alert received:', alert);
      return;
    }

    let actualAlert = alert;
    if (alert['0'] && typeof alert['0'] === 'object') {
      console.log('Alert is wrapped, extracting actual alert data');
      actualAlert = alert['0'];
    }

    const { alertType, payload } = actualAlert;

    console.log(`🔍 Processing alert type: ${alertType}, timestamp: ${actualAlert.timestamp}`);

    if (alertType === 'SOS') {
      console.log('🆘 Skipping SOS alert - handled by AppLayout');
      return;
    }

    if (alertType === 'SensorStatus') {
      console.log('📡 Processing SENSOR STATUS alert');
      handleSensorStatusUpdate(alert);
      return;
    }

    if (!payload || typeof payload !== 'object') {
      console.warn('Alert missing payload:', actualAlert);
      return;
    }

    const { bedId, masterAlarmId, alarmPriority } = payload;

    if (bedId === undefined || bedId === null) {
      console.warn('Alert missing bedId in payload:', actualAlert);
      return;
    }

    console.log('✅ Valid alert - AlertType:', alertType, 'BedId:', bedId, 'Payload:', payload);

    refreshNotifications();

    if (alertType === 'Confirmation') {
      console.log('📝 Processing CONFIRMATION alert for bedId:', bedId);
      handleConfirmation(bedId);
    } else if (alertType === 'Hold') {
      console.log('⏸️ Processing HOLD alert for bedId:', bedId);
      handleHold(bedId);
    } else if (alertType === 'Reset') {
      console.log('🔄 Processing RESET alert for bedId:', bedId);
      handleReset(bedId);
    } else if (alertType === 'HoldExtension') {
      console.log('➕ Processing HOLD EXTENSION alert for bedId:', bedId);
      handleHoldExtension(bedId, payload);
    } else {
      if (masterAlarmId === undefined || alarmPriority === undefined) {
        console.warn('Regular alarm missing required fields:', { masterAlarmId, alarmPriority, alert: actualAlert });
        return;
      }

      console.log(`🚨 Processing ALARM alert: ${alertType} for bedId:`, bedId, 'priority:', alarmPriority);
      handleNewAlarm(bedId, masterAlarmId, alarmPriority, payload);
    }
  };

  const handleSensorStatusUpdate = (alert) => {
    console.log('Processing SensorStatus update:', alert);

    if (alert && typeof alert === 'object' && !Array.isArray(alert)) {
      const numericKeys = Object.keys(alert).filter((key) => !isNaN(key) && key !== 'timestamp' && key !== 'id');

      if (numericKeys.length > 0) {
        console.log('Processing wrapped SensorStatus alerts with numeric keys:', numericKeys);
        numericKeys.forEach((key) => {
          const sensorAlert = alert[key];
          if (sensorAlert && sensorAlert.alertType === 'SensorStatus' && sensorAlert.payload) {
            const { bedId, batPercent, btPercent } = sensorAlert.payload;

            if (bedId !== undefined && bedId !== null) {
              setBedSensorStatus((prev) => ({
                ...prev,
                [bedId]: {
                  batPercent: batPercent || 0,
                  btPercent: btPercent || 0
                }
              }));

              console.log(`Updated sensor status for bed ${bedId}: battery ${batPercent}%, bluetooth ${btPercent}%`);
            }
          }
        });
        return;
      }
    }

    if (Array.isArray(alert)) {
      console.log('Processing array of SensorStatus alerts, length:', alert.length);
      alert.forEach((sensorAlert) => {
        if (sensorAlert.alertType === 'SensorStatus' && sensorAlert.payload) {
          const { bedId, batPercent, btPercent } = sensorAlert.payload;

          if (bedId !== undefined && bedId !== null) {
            setBedSensorStatus((prev) => ({
              ...prev,
              [bedId]: {
                batPercent: batPercent || 0,
                btPercent: btPercent || 0
              }
            }));

            console.log(`Updated sensor status for bed ${bedId}: battery ${batPercent}%, bluetooth ${btPercent}%`);
          }
        }
      });
    } else {
      if (alert.payload && alert.payload.bedId !== undefined && alert.payload.bedId !== null) {
        const { bedId, batPercent, btPercent } = alert.payload;

        setBedSensorStatus((prev) => ({
          ...prev,
          [bedId]: {
            batPercent: batPercent || 0,
            btPercent: btPercent || 0
          }
        }));

        console.log(`Updated sensor status for bed ${bedId}: battery ${batPercent}%, bluetooth ${btPercent}%`);
      }
    }
  };

  const handleConfirmation = (bedId) => {
    try {
      console.log('📝 Handling confirmation for bedId (same as cancel):', bedId);

      setRoomsData((prevRooms) =>
        prevRooms.map((room) =>
          room.id.toString() === bedId.toString()
            ? { ...room, alarmStatus: null }
            : room
        )
      );

      // Check alarm name BEFORE entering the setBedAlarms updater to avoid calling setState inside setState
      const bedAlarmsList = bedAlarms[bedId] || [];
      const currentAlarm = bedAlarmsList[0];
      if (
        currentAlarm?.alarmName &&
        (currentAlarm.alarmName.toLowerCase().includes('diaper') ||
          currentAlarm.alarmName.toLowerCase().includes('wetsafe'))
      ) {
        setBedDiaperStates((s) => ({ ...s, [bedId]: true }));
      }

      setBedAlarms((prev) => {
        const list = prev[bedId] || [];
        if (list.length === 0) {
          console.log('No alarms found for bedId:', bedId);
          return prev;
        }
        const newBedAlarms = { ...prev };
        delete newBedAlarms[bedId];
        console.log('Cleared all alarms for bedId due to confirmation:', bedId);
        return newBedAlarms;
      });

      setBedHoldStates((prev) => {
        const newStates = { ...prev };
        delete newStates[bedId];
        console.log('Confirm clicked - changed hold state to occupied for bedId:', bedId);
        return newStates;
      });
    } catch (error) {
      console.error('Error in handleConfirmation:', error);
    }
  };

  const handleHold = (bedId) => {
    try {
      console.log('Handling hold for bedId:', bedId);

      setBedHoldStates((prev) => ({
        ...prev,
        [bedId]: true
      }));

      setBedAlarms((prev) => {
        if (prev[bedId]) {
          const newBedAlarms = { ...prev };
          delete newBedAlarms[bedId];
          console.log('Cleared alarms due to hold for bedId:', bedId);
          return newBedAlarms;
        }
        return prev;
      });
    } catch (error) {
      console.error('Error in handleHold:', error);
    }
  };

  const handleReset = (bedId) => {
    try {
      console.log('Handling reset for bedId:', bedId);

      setRoomsData((prevRooms) =>
        prevRooms.map((room) =>
          room.id.toString() === bedId.toString()
            ? { ...room, alarmStatus: null }
            : room
        )
      );

      setBedHoldStates((prev) => {
        const newStates = { ...prev };
        delete newStates[bedId];
        console.log('Reset hold state for bedId:', bedId);
        return newStates;
      });

      setBedMedicationStates((prev) => {
        const newStates = { ...prev };
        delete newStates[bedId];
        console.log('Reset medication state for bedId:', bedId);
        return newStates;
      });

      setBedDiaperStates((prev) => {
        const newStates = { ...prev };
        delete newStates[bedId];
        return newStates;
      });
    } catch (error) {
      console.error('Error in handleReset:', error);
    }
  };

  const fetchMedicationDetails = async (medicationReminderId) => {
    try {
      setMedicationPopup((prev) => ({ ...prev, loading: true, error: null }));

      const response = await axiosInstance.get('/MedicActionReminder/get-reminders', {
        params: { medicActionReminderId: medicationReminderId }
      });

      if (response.data && response.data.result) {
        console.log('Medication details fetched:', response.data.result);
        setMedicationPopup((prev) => ({
          ...prev,
          medicationData: response.data.result,
          loading: false
        }));
      } else {
        throw new Error(response.data?.errorResponse?.message || 'Failed to fetch medication details');
      }
    } catch (error) {
      console.error('Error fetching medication details:', error);
      setMedicationPopup((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to fetch medication details'
      }));
    }
  };

  const handleMedicationIconClick = (bedId) => {
    const medicationState = bedMedicationStates[bedId];

    if (medicationState && medicationState.isActive && medicationState.medicationReminderId) {
      console.log('Opening medication popup for bedId:', bedId, 'medicationReminderId:', medicationState.medicationReminderId);

      setMedicationPopup((prev) => ({
        ...prev,
        isOpen: true,
        medicationReminderId: medicationState.medicationReminderId,
        medicationData: null,
        loading: false,
        error: null
      }));

      fetchMedicationDetails(medicationState.medicationReminderId);
    } else {
      console.log('No active medication state for bedId:', bedId);
    }
  };

  const closeMedicationPopup = () => {
    setMedicationPopup({
      isOpen: false,
      medicationReminderId: null,
      medicationData: null,
      loading: false,
      error: null
    });
  };

  const handleManualConfirm = async (bedId) => {
    try {
      const currentAlarms = bedAlarms[bedId] || [];
      const currentAlarm = currentAlarms[0];
      const isMedication = currentAlarm?.alarmName?.toLowerCase().includes('medication');
      const isDiaper =
        currentAlarm?.alarmName?.toLowerCase().includes('diaper') ||
        currentAlarm?.alarmName?.toLowerCase().includes('wetsafe');

      // For medication: open reminder popup immediately (optimistic UX, same as before)
      if (isMedication) {
        console.log('💊 Processing medication alert confirmation for bedId:', bedId);
        const medicationReminderId = currentAlarm.payload?.reminderId;
        if (medicationReminderId) {
          console.log('Found MedicationReminderId:', medicationReminderId);
          setBedMedicationStates((prev) => ({
            ...prev,
            [bedId]: { isActive: true, medicationReminderId }
          }));
          setMedicationPopup((prev) => ({
            ...prev,
            isOpen: true,
            medicationReminderId,
            medicationData: null,
            loading: false,
            error: null
          }));
          fetchMedicationDetails(medicationReminderId);
        } else {
          console.warn('MedicationReminderId not found in alarm payload');
        }
      }

      const alarmManagementModel = { BedId: parseInt(bedId) };
      console.log('Calling confirm-alarm API with:', alarmManagementModel);

      const response = await axiosInstance.post('/AlarmManagement/confirm-alarm', alarmManagementModel);

      if (!response.data?.result) {
        console.error('Backend confirm alarm failed:', response.data?.errorResponse?.message || 'Unknown error');
        // Rollback medication state if API failed
        if (isMedication) {
          setBedMedicationStates((prev) => {
            const next = { ...prev };
            delete next[bedId];
            return next;
          });
          setMedicationPopup({ isOpen: false, medicationReminderId: null, medicationData: null, loading: false, error: null });
        }
        return;
      }

      console.log('Alarm confirmed successfully in backend');

      // Diaper state set ONLY after successful API response
      if (isDiaper) {
        setBedDiaperStates((prev) => ({ ...prev, [bedId]: true }));
      }

      // Remove this bed's alert from activeAlerts so audio stops (same as cancel)
      setActiveAlerts((prev) =>
        prev.filter((a) => {
          const aPayload = a.payload || a['0']?.payload;
          return aPayload?.bedId?.toString() !== bedId.toString();
        })
      );

      setRoomsData((prevRooms) =>
        prevRooms.map((room) =>
          room.id.toString() === bedId.toString()
            ? { ...room, alarmStatus: null }
            : room
        )
      );

      setBedAlarms((prev) => {
        const bedAlarmsList = prev[bedId] || [];
        if (bedAlarmsList.length === 0) return prev;
        const remainingAlarms = bedAlarmsList.slice(1);
        const newBedAlarms = { ...prev };
        if (remainingAlarms.length > 0) {
          newBedAlarms[bedId] = remainingAlarms;
        } else {
          delete newBedAlarms[bedId];
        }
        console.log('Frontend alarm confirmed for bedId:', bedId);
        return newBedAlarms;
      });

      setBedHoldStates((prev) => {
        const newStates = { ...prev };
        delete newStates[bedId];
        console.log('Manual confirm clicked - changed hold state to occupied for bedId:', bedId);
        return newStates;
      });
    } catch (error) {
      console.error('Error confirming alarm:', error);
    }
  };

  const handleAcceptExtension = async (bedId) => {
    try {
      const alarmManagementModel = {
        BedId: parseInt(bedId)
      };

      console.log('Calling extend-alarm-timeout API with:', alarmManagementModel);

      const response = await axiosInstance.post('/AlarmManagement/extend-alarm-timeout', alarmManagementModel);

      if (response.data && response.data.result) {
        console.log('Alarm timeout extended successfully in backend');
      } else {
        console.error('Backend extend alarm timeout failed:', response.data?.errorResponse?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error extending alarm timeout:', error);
    } finally {
      setBedAlarms((prev) => {
        const bedAlarmsList = prev[bedId] || [];
        const remainingAlarms = bedAlarmsList.filter((alarm) => !alarm.isExtension);

        const newBedAlarms = { ...prev };

        if (remainingAlarms.length > 0) {
          newBedAlarms[bedId] = remainingAlarms;
        } else {
          delete newBedAlarms[bedId];
        }

        console.log('Hold Extension alarm cleared for bedId:', bedId);
        return newBedAlarms;
      });
    }
  };

  const clearProcessedAlerts = () => {
    // console.log('🧹 MyPatientsPage - Local processing tracking active');
  };

  const handleManualCancel = (bedId) => {
    console.log('Cancelling alarms for bedId:', bedId);

    setRoomsData((prevRooms) =>
      prevRooms.map((room) =>
        room.id.toString() === bedId.toString()
          ? { ...room, alarmStatus: null }
          : room
      )
    );

    // Remove this bed's alert from activeAlerts so audio stops immediately
    setActiveAlerts((prev) =>
      prev.filter((a) => {
        const aPayload = a.payload || a['0']?.payload;
        return aPayload?.bedId?.toString() !== bedId.toString();
      })
    );

    setBedAlarms((prev) => {
      const bedAlarmsList = prev[bedId] || [];
      if (bedAlarmsList.length === 0) return prev;

      const alarmToCancel = bedAlarmsList[0];
      if (alarmToCancel.isExtension) {
        return handleCancelExtension(bedId);
      }

      const newBedAlarms = { ...prev };
      delete newBedAlarms[bedId];

      console.log('All alarms cancelled for bedId:', bedId);
      return newBedAlarms;
    });
  };

  const handleCancelExtension = (bedId) => {
    console.log('Cancelling Hold Extension for bedId:', bedId);

    setBedAlarms((prev) => {
      const bedAlarmsList = prev[bedId] || [];
      const remainingAlarms = bedAlarmsList.filter((alarm) => !alarm.isExtension);

      const newBedAlarms = { ...prev };

      if (remainingAlarms.length > 0) {
        newBedAlarms[bedId] = remainingAlarms;
      } else {
        delete newBedAlarms[bedId];
      }

      console.log('Hold Extension alarm cancelled for bedId:', bedId);
      return newBedAlarms;
    });
  };

  const filteredRooms = roomsData.filter((bed) => {
    const isActiveRoomManagementBed = activeRoomManagementBedId?.toString() === bed.id?.toString();

    if (showOnlyRoomsWithPatients && !bed.patientId && !isActiveRoomManagementBed) {
      return false;
    }

    if (roleId === '2' && selectedDepartment !== 'all') {
      return String(bed.departmentId) === String(selectedDepartment);
    }

    return true;
  });

  const sortedRooms = [...filteredRooms].sort((a, b) => {
    const alarmsA = bedAlarms[a.id] || [];
    const alarmsB = bedAlarms[b.id] || [];

    const hasAlarmsA = alarmsA.length > 0;
    const hasAlarmsB = alarmsB.length > 0;

    if (hasAlarmsA && hasAlarmsB) {
      const minPriorityA = Math.min(...alarmsA.map((alarm) => Number(alarm.priority)));
      const minPriorityB = Math.min(...alarmsB.map((alarm) => Number(alarm.priority)));
      return minPriorityA - minPriorityB;
    }

    if (hasAlarmsA) return -1;
    if (hasAlarmsB) return 1;

    return 0;
  });

  if (loading) {
    return <Flex justify="center">Loading...</Flex>;
  }

  if (error) {
    return <Flex justify="center">{error}</Flex>;
  }

  return (
    <Flex direction="column" gap="3">
      <div className="sticky -top-7 z-10 bg-[#e8e8e8] py-3 -mt-7 pt-7 -mx-4 px-4 mb-3 shadow-sm border-b border-gray-300">
        <Flex justify="between" align="center" gap="3" wrap="wrap">
          <Flex align="center" gap="5" wrap="wrap">
            {roleId === '2' && (
              <Flex align="center" gap="2">
                <Text size="2" weight="bold">Department</Text>
                <Select.Root value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <Select.Trigger style={{ width: '220px' }} placeholder="All Departments" />
                <Select.Content position="popper">
                  <Select.Group>
                    <Select.Item value="all">All Departments</Select.Item>
                    {departments.map((dept) => (
                      <Select.Item key={dept.key} value={dept.key}>
                        {dept.value}
                        </Select.Item>
                      ))}
                    </Select.Group>
                  </Select.Content>
                </Select.Root>
              </Flex>
            )}
            <Text as="label" size="2">
              <Flex gap="2" align="center">
                <Switch
                  checked={showOnlyRoomsWithPatients}
                  onCheckedChange={setShowOnlyRoomsWithPatients}
                  size="1"
                />
                Only occupied rooms
              </Flex>
            </Text>
          </Flex>

          <Flex align="center" gap="3">
            {roleId === '3' && <CreateRoom onRoomCreated={fetchBeds} />}
            <SOSButton />
          </Flex>
        </Flex>
      </div>

      <Grid columns={{ initial: '1', xs: '2', sm: '2', md: '4', lg: '5' }} gap="4" width="100%">
        {sortedRooms.map((bed) => (
          <RoomCard
            key={bed.id}
            bedData={bed}
            refreshBeds={() => fetchBeds({ silent: true })}
            onManagementOpenChange={(isOpen) => {
              setActiveRoomManagementBedId(isOpen ? bed.id : null);
            }}
            bedAlarms={bedAlarms[bed.id] || []}
            isOnHold={bedHoldStates[bed.id] || false}
            sensorStatus={bedSensorStatus[bed.id] || { batPercent: 0, btPercent: 0 }}
            medicationState={bedMedicationStates[bed.id] || { isActive: false, medicationReminderId: null }}
            diaperState={bedDiaperStates[bed.id] || false}
            onConfirmAlarm={() => handleManualConfirm(bed.id)}
            onCancelAlarm={() => handleManualCancel(bed.id)}
            onMedicationIconClick={() => handleMedicationIconClick(bed.id)}
            onAcceptExtension={() => handleAcceptExtension(bed.id)}
            onCancelExtension={() => handleCancelExtension(bed.id)}
          />
        ))}
      </Grid>

      <MedicationPopup
        isOpen={medicationPopup.isOpen}
        onClose={closeMedicationPopup}
        medicationData={medicationPopup.medicationData}
        loading={medicationPopup.loading}
        error={medicationPopup.error}
      />
    </Flex>
  );
};

export default MyPatientsPage;
